import { useState } from 'react'
import rangeParser from 'parse-numeric-range'
// https://github.com/FormidableLabs/prism-react-renderer/tree/master/packages/prism-react-renderer/src/themes
import Highlight, { defaultProps, Language } from 'prism-react-renderer'
import Editor from 'react-simple-code-editor'
import theme from 'prism-react-renderer/themes/shadesOfPurple'
import clsx from 'clsx'
import { CopyButton } from '../CopyButton'
// import { JetBrains_Mono } from 'next/font/google'

// const JetBrainsMono = JetBrains_Mono({
//   subsets: ['latin'],
//   weight: ['300', '400', '500', '700'],
//   display: 'swap',
// })

// Create a closure that determines if we have
// to highlight the given index
const calculateLinesToHighlight = (
  meta: string
): [(index: number) => boolean, boolean] => {
  const RE = /{([\d,-]+)}/
  if (RE.test(meta)) {
    const match = RE.exec(meta)
    if (match) {
      const strlineNumbers = match[1]
      const lineNumbers = rangeParser(strlineNumbers)
      return [(index: number) => lineNumbers.includes(index + 1), true]
    }
  }
  return [() => false, false]
}

export function Fence({
  children,
  onChange,
  language,
  queryType = '',
  highlights = '',
  shouldWrap = false,
  includeCopyButton = true,
  isExpandable = false,
  maxVisibleLines = 15,
  className = {
    'bg-color': '!bg-[#0A0B0C]',
  },
  topRightElements = (CopyButton) => <>{CopyButton}</>,
}: {
  children: string
  onChange?: (code: string) => void
  language?: Language
  queryType?: string
  highlights?: string
  shouldWrap?: boolean
  includeCopyButton?: boolean
  isExpandable?: boolean
  maxVisibleLines?: number
  className?: {
    'bg-color'?: string
    container?: string
    code?: string
  }
  topRightElements?: (CopyButton: React.ReactNode) => React.ReactNode
}) {
  const [shouldHighlightLine, hasHighlights] =
    calculateLinesToHighlight(highlights)
  const [hovered, setHovered] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const codeText = children.trimEnd()
  const code = children.trimEnd()

  // Count the number of lines in the code
  const lineCount = code.split('\n').length
  const shouldShowExpand = isExpandable && lineCount > maxVisibleLines

  if (queryType == 'INSERT' && codeText.length > 20000) {
    language = undefined
  }

  function highlight(code: string) {
    return (
      <Highlight
        {...defaultProps}
        code={code.trimEnd()}
        // @ts-ignore
        language={language}
        theme={theme}
      >
        {({ className, tokens, getLineProps, getTokenProps }) => {
          // If expandable and not expanded, limit the tokens to show
          const displayTokens =
            shouldShowExpand && !isExpanded
              ? tokens.slice(0, maxVisibleLines)
              : tokens

          return (
            <pre
              className={clsx(
                `${className}`,
                'm-0 !rounded-none !bg-[#0A0B0C] p-0 !ring-0'
              )}
            >
              <code
                className={clsx(
                  shouldWrap ? 'whitespace-pre-wrap break-words' : ''
                )}
              >
                {displayTokens.map((line, lineIndex) => {
                  const lineProps = getLineProps({ line, key: lineIndex })
                  if (!shouldHighlightLine(lineIndex) && hasHighlights) {
                    lineProps.className = `${lineProps.className} opacity-70`
                  } else if (shouldHighlightLine(lineIndex)) {
                    lineProps.className = `${lineProps.className} pl-2 border-l-4 border-sky-400 bg-sky-300/[0.15]`
                  }
                  return (
                    <div key={lineIndex} {...lineProps}>
                      {line
                        .filter((token) => !token.empty)
                        .map((token, tokenIndex) => (
                          <span
                            key={tokenIndex}
                            {...getTokenProps({ token })}
                          />
                        ))}
                      {'\n'}
                    </div>
                  )
                })}
              </code>
            </pre>
          )
        }}
      </Highlight>
    )
  }

  return (
    <div
      className={clsx(
        `!relative overflow-auto rounded-2xl bg-[#0A0B0C] ring-1 ring-gray-100/10`,
        className.container,
        className['bg-color']
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="sticky top-0 z-10">
        <div className={`absolute right-3 top-[18px]`}>
          {includeCopyButton &&
            topRightElements(
              <CopyButton hovered={hovered} content={codeText} size={4} />
            )}
        </div>
      </div>
      <div className="h-full">
        <div
          className={clsx(className.code, 'p-3 pr-5', isExpanded && 'pb-16')}
        >
          {onChange ? (
            <Editor
              value={code}
              onValueChange={onChange}
              highlight={highlight}
              className={clsx(`font-mono caret-white`, className.code)}
            />
          ) : (
            highlight(code)
          )}
        </div>
      </div>
      {shouldShowExpand && (
        <>
          <div
            className={`pointer-events-none absolute inset-x-0 bottom-0 mx-auto h-40 w-full bg-gradient-to-t ${
              isExpanded ? 'from-transparent' : 'from-[#0A0B0C]'
            } to-transparent`}
          />

          <div
            onClick={() => setIsExpanded(!isExpanded)}
            className="absolute inset-x-0 bottom-8 z-10 mx-auto flex w-max cursor-pointer justify-center rounded-lg border-slate-700 bg-white/20 py-1.5 pl-2 pr-3 backdrop-blur-sm"
          >
            <button className="flex items-center justify-center gap-2 text-sm text-white">
              {isExpanded ? (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="duration-400 rotate-180 transition-transform"
                  >
                    <path d="M12 5v14" className=""></path>
                    <path d="m19 12-7 7-7-7" className=""></path>
                  </svg>
                  Collapse Code
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="duration-400 transition-transform"
                  >
                    <path d="M12 5v14" className=""></path>
                    <path d="m19 12-7 7-7-7" className=""></path>
                  </svg>
                  Expand Code
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
